import React from "react"
import { Box, Container, Grid, List, ListItem, ListItemText } from "@mui/material"

import Link from "components/i18n/Link"

import SEO from "@secureo/common/components/SEO"
import BreadCrumbs, { BreadCrumb } from "components/BreadCrumbs"

import { helpCenterAncestorBreadCrumbs } from "./index"
import { fallBackPhoneNumber } from "config/contact"

import { CustomAppProps } from "pages/_app"

const overviewLinks = [
	{
		title: "§ 1 Grundlegende Bestimmungen",
		anchorLink: "#grundlegende-bestimmungen",
	},
	{
		title: "$ 2 Zustandekommen des Vertrages",
		anchorLink: "#zustandekommen-des-vertrages",
	},
	{
		title: "$ 3 Individuell gestaltete Waren",
		anchorLink: "#individuell-gestaltete-waren",
	},
	{
		title: "$ 4 Zurückbehaltungsrecht, Eigentumsvorbehalt",
		anchorLink: "#zurueckbehaltungsrecht",
	},
	{
		title: "$ 5 Gewährleistung",
		anchorLink: "#gewaehrleistung",
	},
	{
		title: "$ 6 Rechtswahl, Erfüllungsart, Gerichtsstand",
		anchorLink: "#rechtswahl-erfuellungsart-gerichtsstand",
	},
	{
		title: "1. Identität des Verkäufers",
		anchorLink: "#identitaet-des-verkaeufers",
	},
	{
		title: "2. Informationen zum Zustandekommen des Vertrages",
		anchorLink: "#informationen-zum-zustandekommen-des-vertrages",
	},
	{
		title: "3. Vertragssprache, Vertragstextspeicherung",
		anchorLink: "#vertragssprache-vertragstextspeicherung",
	},
	{
		title: "4. Verhaltenskodizes",
		anchorLink: "#verhaltenskodizes",
	},
	{
		title: "5. Wesentliche Merkmale der Ware oder Dienstleistung",
		anchorLink: "#merkmale-ware-oder-dienstleistung",
	},
	{
		title: "6. Preise und Zahlungsmodalitäten",
		anchorLink: "#preise-und-zahlungsmodalitaeten",
	},
	{
		title: "7. Lieferbedingungen",
		anchorLink: "#lieferbedingungen",
	},
	{
		title: "8. Gesetzliche Mängelhaftungsrecht",
		anchorLink: "#gesetzliche-maengelhaftungsrecht",
	},
	{
		title: "9. Zahlung per Rechnung und Finanzierung",
		anchorLink: "#zahlung-per-rechnung-und-finanzierung",
	},
]

const TermsAndConditionsPage = ({ asPath, locale }: CustomAppProps) => {
	const currentPath: BreadCrumb = {
		href: "/help/terms-and-conditions",
		label: "Allgemeine Geschäftsbedingungen",
	}

	return (
		<>
			<SEO
				asPath={asPath}
				appendLocalizedShopNameWithPipe
				title="Allgemeine Geschäftsbedingungen"
			/>

			<div style={{ position: "relative", overflow: "hidden" }}>
				<img
					alt="background"
					src="https://res.cloudinary.com/digitalzylinder-shop/image/upload/v1590648703/DigiShop2/Content/Imprint-Bg-Circle.png"
					style={{
						position: "absolute",
						width: "100%",
						height: 500,
						top: 0,
						left: 0,
						zIndex: -1,
					}}
				/>
				<Box mt={2}>
					<BreadCrumbs
						locale={locale}
						currentPath={currentPath}
						ancestors={helpCenterAncestorBreadCrumbs}
					/>
				</Box>
				<Box my={5}>
					<Container>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<div>
									<div
										style={{
											color: "#4691CE",
											textTransform: "uppercase",
											fontSize: 13,
										}}
									>
										<strong>
											Geschäftsbedingungen und Kundeninformationen
										</strong>
									</div>
									<Box mb={2}>
										<h1 style={{ margin: 0, fontSize: 30 }}>
											Allgemeine Geschäftsbedingungen
										</h1>
									</Box>
								</div>
							</Grid>
							<Grid item xs={12} md={3}>
								<Box mt={3}>
									<List component="nav" aria-label="overview-agb">
										<ListItem>
											<div className="h2" style={{ fontSize: 26 }}>
												Übersicht
											</div>
										</ListItem>
										{overviewLinks.map((overviewLink) => {
											const { title, anchorLink } = overviewLink
											return (
												<a href={anchorLink} key={title}>
													<ListItem button>
														<ListItemText
															primary={title}
															style={{ color: "#505e5f" }}
														/>
													</ListItem>
												</a>
											)
										})}
									</List>
								</Box>
							</Grid>
							<Grid item xs={12} md={9}>
								<Box mt={5}>
									<h2>I. Allgemeine Geschäftsbedingungen</h2>
								</Box>
								<Box my={3} id="grundlegende-bestimmungen">
									<strong>§ 1 Grundlegende Bestimmungen</strong>
								</Box>
								<p>
									(1) Die nachstehenden Geschäftsbedingungen gelten für alle
									Verträge, die Sie mituns als Anbieter (secureo GmbH),
									nachfolgend „Verkäufer“, über die Internetseite
									digitalzylinder-shop.com, nachfolgend „Online-Shop“ schließen.
									Soweit nicht anders vereinbart, wird der Einbeziehung
									gegebenenfalls von Ihnen verwendeter eigener Bedingungen
									widersprochen.
								</p>
								<Box mt={3}>
									<p>
										(2) Verbraucher im Sinne der nachstehenden Regelungen ist
										jede natürliche Person, die ein Rechtsgeschäft zu Zwecken
										abschließt, die überwiegend weder ihrer gewerblichen noch
										ihrer selbständigen beruflichen Tätigkeit zugerechnet werden
										kann. Unternehmer ist jede natürliche oder juristische
										Person oder eine rechtsfähige Personengesellschaft, die bei
										Abschluss eines Rechtsgeschäfts in Ausübung ihrer
										selbständigen beruflichen oder gewerblichen Tätigkeit
										handelt.
									</p>
								</Box>
								<Box my={3} id="zustandekommen-des-vertrages">
									<strong>§ 2 Zustandekommen des Vertrages</strong>
								</Box>
								<Box mb={3}>
									<p>
										(1) Gegenstand des Vertrages ist der Verkauf von Waren und
										anderen Leistungennach Kundenspezifikation, wie
										beispielsweise die individuelle Anfertigung von Schlössern,
										Zylindern, Schlüsseln und Ähnlichem nach Kundenwunsch,
										welche der Verkäufer in seinem Online-Shop anbietet.
									</p>
								</Box>
								<p>
									(2) Die im Online-Shop des Verkäufers enthaltenen
									Produktbeschreibungen stellen keine verbindlichen Angebote
									seitens des Verkäufers dar, sondern dienen zur Abgabe eines
									verbindlichen Angebots durch den Kunden.
								</p>
								<Box my={3}>
									<p>
										(3) Der Kunde kann das Angebot über das in den Online-Shop
										des Verkäufers integrierte Online-Bestellformular abgeben.
										Dabei gibt der Kunde, nachdem er dieausgewählten Waren in
										den virtuellen Warenkorb gelegt und den elektronischen
										Bestellprozess durchlaufen hat, durch Klicken des den
										Bestellvorgang abschließenden Buttons ein rechtlich
										verbindliches Vertragsangebot in Bezug auf die im Warenkorb
										enthaltenen Waren ab.
									</p>
								</Box>
								<Box my={3}>
									<p>
										(4) Der Verkäufer kann das Angebot des Kunden innerhalb von
										fünf Tagen annehmen,
									</p>
									<p>
										- indem er dem Kunden eine schriftliche Auftragsbestätigung
										oder eine Auftragsbestätigung in Textform (Brief, Fax oder
										E-Mail) übermittelt, wobei insoweit der Zugang der
										Auftragsbestätigung beim Kunden maßgeblich ist, oder
									</p>
									<p>
										- indem er dem Kunden die bestellte Ware liefert, wobei
										insoweit der Zugang der Ware beim Kunden maßgeblich ist,
										oder
									</p>
									<p>
										- indem er den Kunden nach Abgabe von dessen Bestellung zur
										Zahlung auffordert.
									</p>
									<p>
										Liegen mehrere der vorgenannten Alternativen vor, kommt der
										Vertrag in dem Zeitpunkt zustande, in dem eine der
										vorgenannten Alternativen zuerst eintritt. Die Frist zur
										Annahme des Angebots beginnt am Tag nach der Absendung des
										Angebots durch den Kunden zu laufen und endet mit dem Ablauf
										des fünften Tages, welcher auf die Absendung des Angebots
										folgt. Nimmt der Verkäufer das Angebot des Kunden innerhalb
										vorgenannter Frist nicht an, so gilt dies als Ablehnung des
										Angebots mit der Folge, dass der Kunde nicht mehr an seine
										Willenserklärung gebunden ist.
									</p>
								</Box>
								<p>
									(5) Ihre Anfragen zur Erstellung eines Angebotes sind für Sie
									unverbindlich. Wir unterbreiten Ihnen hierzu ein verbindliches
									Angebot in Textform (z.B. per E-Mail), welches Sie innerhalb von
									5 Tagen annehmen können.
								</p>
								<p>
									(6) Die Abwicklung der Bestellung und Übermittlung aller im
									Zusammenhang mit dem Vertragsschluss erforderlichen
									Informationen erfolgt per E-Mail zum Teil automatisiert. Sie
									haben deshalb sicherzustellen, dass die von Ihnen bei uns
									hinterlegte E-Mail-Adresse zutreffend ist, der Empfang der
									E-Mails technisch sichergestellt und insbesondere nicht durch
									SPAM-Filter verhindert wird.
								</p>
								<Box my={3} id="individuell-gestaltete-waren">
									<strong>§ 3 Individuell gestaltete Waren</strong>
								</Box>
								<p>
									(1) Sie stellen uns die für die individuelle Gestaltung der
									Waren erforderlichen geeigneten Informationen, Texte oder
									Dateien über das Online-Bestellsystem oder per E-Mail spätestens
									unverzüglich nach Vertragsschluss zur Verfügung. Unsere etwaigen
									Vorgaben zu Dateiformaten sind zu beachten.
								</p>
								<Box my={3}>
									<p>
										(2) Sie verpflichten sich, keine Daten zu übermitteln, deren
										Inhalt Rechte Dritter (insbesondere Urheberrechte,
										Namensrechte, Markenrechte) verletzen oder gegen bestehende
										Gesetze verstoßen. Sie stellen uns ausdrücklich von
										sämtlichen in diesem Zusammenhang geltend gemachten
										Ansprüchen Dritter frei. Das betrifft auch die Kosten der in
										diesem Zusammenhang erforderlichen rechtlichen Vertretung.
									</p>
								</Box>
								<p>
									(3) Wir nehmen keine Prüfung der übermittelten Daten auf
									inhaltliche Richtigkeit vor und übernehmen insoweit keine
									Haftung für Fehler.
								</p>
								<Box my={3} id="zurueckbehaltungsrecht">
									<strong>§ 4 Zurückbehaltungsrecht, Eigentumsvorbehalt</strong>
								</Box>
								<p>
									(1) Ein Zurückbehaltungsrecht können Sie nur ausüben, soweit es
									sich um Forderungen aus demselben Vertragsverhältnis handelt.
								</p>
								<Box my={3}>
									<p>
										(2) Die Ware bleibt bis zur vollständigen Zahlung des
										Kaufpreises unser Eigentum.
									</p>
								</Box>
								<p>(3) Sind Sie Unternehmer, gilt ergänzend Folgendes:</p>

								<Box my={3}>
									<p>
										a) Wir behalten uns das Eigentum an der Ware bis zum
										vollständigen Ausgleich aller Forderungen aus der laufenden
										Geschäftsbeziehung vor. Vor Übergang des Eigentums an der
										Vorbehaltsware ist eine Verpfändung oder
										Sicherheitsübereignung nicht zulässig.
									</p>
								</Box>
								<p>
									b) Sie können die Ware im ordentlichen Geschäftsgang
									weiterverkaufen. Für diesen Fall treten Sie bereits jetzt alle
									Forderungen in Höhe des Rechnungsbetrages, die Ihnen aus dem
									Weiterverkauf erwachsen, an uns ab, wir nehmen die Abtretung an.
									Sie sind weiter zur Einziehung der Forderung ermächtigt. Soweit
									Sie Ihren Zahlungsverpflichtungen nicht ordnungsgemäß
									nachkommen, behalten wir uns allerdings vor, die Forderung
									selbst einzuziehen.
								</p>
								<Box my={3}>
									<p>
										c) Bei Verbindung und Vermischung der Vorbehaltsware
										erwerben wir Miteigentum an der neuen Sache im Verhältnis
										des Rechnungswertes der Vorbehaltsware zu den anderen
										verarbeiteten Gegenständen zum Zeitpunkt der Verarbeitung.
									</p>
								</Box>
								<p>
									d) Wir verpflichten uns, die uns zustehenden Sicherheiten auf
									Ihr Verlangen insoweit freizugeben, als der realisierbare Wert
									unserer Sicherheiten die zu sichernde Forderung um mehr als 10%
									übersteigt. Die Auswahl der freizugebenden Sicherheiten obliegt
									uns.
								</p>
								<Box my={3} id="gewaehrleistung">
									<strong>§ 5 Gewährleistung</strong>
								</Box>
								<p>(1) Es bestehen die gesetzlichen Mängelhaftungsrechte.</p>
								<Box my={3}>
									<p>
										(2) Als Verbraucher werden Sie gebeten, die Sache bei
										Lieferung umgehend auf Vollständigkeit, offensichtliche
										Mängel und Transportschäden zu überprüfen und uns sowie dem
										Beförderer (Paketdienst, Spediteur, u. Ä.) Beanstandungen
										schnellstmöglich mitzuteilen. Kommen Sie dem nicht nach, hat
										dies keine Auswirkung auf Ihre gesetzlichen
										Gewährleistungsansprüche.
									</p>
								</Box>
								<p>
									(3) Soweit Sie Unternehmer sind, gilt abweichend von den
									vorstehenden Gewährleistungsregelungen:
								</p>
								<Box my={3}>
									<p>
										a) Als Beschaffenheit der Sache gelten nur unsere eigenen
										Angaben und die Produktbeschreibung des Herstellers als
										vereinbart, nicht jedoch sonstige Werbung, öffentliche
										Anpreisungen und Äußerungen des Herstellers.
									</p>
								</Box>
								<p>
									b) Bei Mängeln leisten wir nach unserer Wahl Gewähr durch
									Nachbesserung oder Nachlieferung. Schlägt die Mangelbeseitigung
									fehl, können Sie nach Ihrer Wahl Minderung verlangen oder vom
									Vertrag zurücktreten. Die Mängelbeseitigung gilt nach
									erfolglosem zweiten Versuch als fehlgeschlagen, wenn sich nicht
									insbesondere aus der Art der Sache oder des Mangels oder den
									sonstigen Umständen etwas anderes ergibt. Im Falle der
									Nachbesserung müssen wir nicht die erhöhten Kosten tragen, die
									durch die Verbringung der Ware an einen anderen Ort als den
									Erfüllungsort entstehen, sofern die Verbringung nicht dem
									bestimmungsgemäßen Gebrauch der Ware entspricht.
								</p>
								<Box my={3}>
									<p>
										c) Die Gewährleistungsfrist beträgt ein Jahr ab Ablieferung
										der Ware. Die Fristverkürzung gilt nicht:
									</p>
								</Box>
								<p>
									- für uns zurechenbare schuldhaft verursachte Schäden aus der
									Verletzung des Lebens, des Körpers oder der Gesundheit und bei
									vorsätzlich oder grob fahrlässig verursachten sonstigen Schäden;
								</p>
								<Box my={3}>
									<p>
										- soweit wir den Mangel arglistig verschwiegen oder eine
										Garantie für die Beschaffenheit der Sache übernommen haben;
									</p>
								</Box>
								<p>
									- bei Sachen, die entsprechend ihrer üblichen Verwendungsweise
									für ein Bauwerk verwendet worden sind und dessen
									Mangelhaftigkeit verursacht haben;
								</p>
								<Box mt={3}>
									<p>
										- bei gesetzlichen Rückgriffsansprüchen, die Sie im
										Zusammenhang mit Mängelrechten gegen uns haben.
									</p>
								</Box>
								<Box my={3} id="rechtswahl-erfuellungsart-gerichtsstand">
									<strong>§ 6 Rechtswahl, Erfüllungsort, Gerichtsstand</strong>
								</Box>
								<p>
									(1) Es gilt deutsches Recht. Bei Verbrauchern gilt diese
									Rechtswahl nur, soweit hierdurch der durch zwingende
									Bestimmungen des Rechts des Staates des gewöhnlichen
									Aufenthaltes des Verbrauchers gewährte Schutz nicht entzogen
									wird (Günstigkeitsprinzip).
								</p>
								<Box my={3}>
									<p>
										(2) Erfüllungsort für alle Leistungen aus den mit uns
										bestehenden Geschäftsbeziehungen sowie Gerichtsstand ist
										unser Sitz, soweit Sie nicht Verbraucher, sondern Kaufmann,
										juristische Person des öffentlichen Rechts oder
										öffentlich-rechtliches Sondervermögen sind. Dasselbe gilt,
										wenn Sie keinen allgemeinen Gerichtsstand in Deutschland
										oder der EU haben oder der Wohnsitz oder gewöhnliche
										Aufenthalt im Zeitpunkt der Klageerhebung nicht bekannt ist.
										Die Befugnis, auch das Gericht an einem anderen gesetzlichen
										Gerichtsstand anzurufen, bleibt hiervon unberührt.
									</p>
								</Box>
								<p>
									(3) Die Bestimmungen des UN-Kaufrechts finden ausdrücklich keine
									Anwendung.
								</p>
								<Box my={3} id="rechtswahl-erfuellungsart-gerichtsstand">
									<strong>§ 7 Widerrufsrecht</strong>
								</Box>
								<p>(1) Verbrauchern steht grundsätzlich ein Widerrufsrecht zu.</p>
								<p>
									(2) Unternehmern kann vom Verkäufer im Einzelfall ein
									Widerrufsrecht auf Kulanzbasis eingeräumt werden.
								</p>
								<p>
									(3) Nähere Informationen zum Widerrufsrecht ergeben sich aus der
									Widerrufsbelehrung des Verkäufers.
								</p>
								<p>
									(4) Das Widerrufsrecht gilt nicht für Verbraucher, die zum
									Zeitpunkt des Vertragsschlusses keinem Mitgliedstaat der
									Europäischen Union angehören und deren alleiniger Wohnsitz und
									Lieferadresse zum Zeitpunkt des Vertragsschlussesaußerhalb der
									Europäischen Union liegen.
								</p>
								<p>
									(5) Das Widerrufsrecht gilt ferner nicht für auf individuellen
									Kundenwunsch angefertigte oder beschaffte Ware. Der Kunde tätigt
									eine individuelle Auswahl, sobald er von den Voreinstellungen
									auf der jeweiligen Produktseite abweicht.
								</p>
								<p>
									(6) Im Fall der Rücksendung der Ware auf Grund eines
									Widerrufsrechtes trägt derKunde die Kosten der Rücksendung.
								</p>
								<Box my={3} id="rechtswahl-erfuellungsart-gerichtsstand">
									<strong>§ 8 Registrierung als Nutzer</strong>
								</Box>
								<p>
									(1) Zur Nutzung des Online-Shops kann sich der Kunde ein
									Nutzerkonto einrichten. Dies erfolgt kostenlos. Hierzu füllt der
									Kunde elektronisch das im Online-Shop des Verkäufers vorhandene
									Anmeldeformular aus. Teilnahmeberechtigt sind ausschließlich
									unbeschränkt geschäftsfähige Käufer. Die für die Anmeldung
									erforderlichen Daten sind von dem Kunden vollständig und
									wahrheitsgemäß anzugeben.
								</p>
								<p>
									(2) Der Kunde ist verpflichtet, das Passwort geheim zu halten
									und dieses Dritten keinesfalls zugänglich zu machen.
								</p>
								<p>
									(3) Soweit sich die persönlichen Angaben des Kunden ändern, ist
									er selbst für deren Aktualisierung verantwortlich. Alle
									Änderungen können online auf der Website von
									&#x22;Digitalzylinder-Shop.com&#x22; unter &#x22;Account&#x22;
									vorgenommen werden.
								</p>
								<Box mt={5} mb={3} id="identitaet-des-verkaeufers">
									<h3>II. Kundeninformationen</h3>
								</Box>
								<h4>1. Identität des Verkäufers</h4>
								<Box my={4}>
									<div>secureo GmbH</div>
									<div>Im Moos 19</div>
									<div>6067 Absam</div>
									<div>Österreich</div>
									<div>
										<strong>Telefon:</strong> {fallBackPhoneNumber}
									</div>
									<div>
										<strong>E-Mail:</strong> office@secureo.at
									</div>
								</Box>
								<p>Alternative Streitbeilegung:</p>
								<p>
									Die Europäische Kommission stellt eine Plattform für die
									außergerichtliche Online-Streitbeilegung (OS-Plattform) bereit,
									aufrufbar unter
									<a
										href="https://ec.europa.eu/odr"
										target="_blank"
										rel="noopener noreferrer"
									>
										https://ec.europa.eu/odr
									</a>
									.
								</p>
								<Box my={3} id="informationen-zum-zustandekommen-des-vertrages">
									<h4>2. Informationen zum Zustandekommen des Vertrages</h4>
								</Box>
								<p>
									Die technischen Schritte zum Vertragsschluss, der
									Vertragsschluss selbst und die Korrekturmöglichkeiten erfolgen
									nach Maßgabe der Regelungen &quot;Zustandekommen des
									Vertrages&quot; unserer Allgemeinen Geschäftsbedingungen (Teil
									I.).
								</p>
								<Box my={3} id="vertragssprache-vertragstextspeicherung">
									<h4>3. Vertragssprache, Vertragstextspeicherung</h4>
								</Box>
								<p>3.1. Vertragssprache ist deutsch.</p>
								<Box my={3}>
									<p>
										3.2. Der vollständige Vertragstext wird von uns nicht
										gespeichert. Vor Absenden der Bestellung über das Online -
										Warenkorbsystem können die Vertragsdaten über die
										Druckfunktion des Browsers ausgedruckt oder elektronisch
										gesichert werden. Nach Zugang der Bestellung bei uns werden
										die Bestelldaten, die gesetzlich vorgeschriebenen
										Informationen bei Fernabsatzverträgen und die Allgemeinen
										Geschäftsbedingungen nochmals per E-Mail an Sie übersandt.
									</p>
								</Box>
								<p>
									3.3. Bei Angebotsanfragen außerhalb des Online-Warenkorbsystems
									erhalten Sie alle Vertragsdaten im Rahmen eines verbindlichen
									Angebotes in Textform übersandt, z.B. per E-Mail, welche Sie
									ausdrucken oder elektronisch sichern können.
								</p>
								<Box my={3} id="verhaltenskodizes">
									<h4>4. Verhaltenskodizes</h4>
								</Box>
								<p>
									4.1. Wir haben uns dem Ehrenkodex der Trusted Shops GmbH
									unterworfen, einsehbar unter:{" "}
									<a
										href="http://www.trustedshops.com/tsdocument/TS_QUALITY_CRITERIA_de.pdf"
										target="_blank"
										rel="noopener noreferrer"
									>
										TrustedShops
									</a>
									.
								</p>
								<Box my={3} id="merkmale-ware-oder-dienstleistung">
									<h4>5. Wesentliche Merkmale der Ware oder Dienstleistung</h4>
								</Box>
								<p>
									Die wesentlichen Merkmale der Ware und/oder Dienstleistung
									finden sich im jeweiligen Angebot.
								</p>
								<Box my={3} id="preise-und-zahlungsmodalitaeten">
									<h4>6. Preise und Zahlungsmodalitäten</h4>
								</Box>
								<p>
									6.1. Die in den jeweiligen Angeboten angeführten Preise sowie
									die Versandkosten stellen Gesamtpreise dar. Sie beinhalten alle
									Preisbestandteile einschließlich aller anfallenden Steuern.
								</p>
								<Box my={3}>
									<p>
										6.2. Die anfallenden Versandkosten sind nicht im Kaufpreis
										enthalten. Sie sind über eine entsprechend bezeichnete
										Schaltfläche auf unserer Internetpräsenz oder im jeweiligen
										Angebot aufrufbar, werden im Laufe des Bestellvorganges
										gesondert ausgewiesen und sind von Ihnen zusätzlich zu
										tragen, soweit nicht die versandkostenfreie Lieferung
										zugesagt ist.
									</p>
								</Box>
								<p>
									6.3. Erfolgt die Lieferung in Länder außerhalb der Europäischen
									Union können von uns nicht zu vertretende weitere Kosten
									anfallen, wie z.B. Zölle, Steuern oder Geldübermittlungsgebühren
									(Überweisungs- oder Wechselkursgebühren der Kreditinstitute),
									die von Ihnen zu tragen sind. Entstandene Kosten der
									Geldübermittlung sind von Ihnen auch in den Fällen zu tragen, in
									denen die Lieferung in einen EU-Mitgliedsstaat erfolgt, die
									Zahlung aber außerhalb der Europäischen Union veranlasst wurde.
								</p>
								<Box my={3}>
									<p>
										6.4. Die Ihnen zur Verfügung stehenden Zahlungsarten sind
										unter einer entsprechend bezeichneten Schaltfläche auf
										unserer Internetpräsenz oder im jeweiligen Angebot
										ausgewiesen.
									</p>
								</Box>
								<p>
									6.5. Soweit bei den einzelnen Zahlungsarten nicht anders
									angegeben, sind die Zahlungsansprüche aus dem geschlossenen
									Vertrag sofort zur Zahlung fällig.
								</p>
								<Box my={3} id="lieferbedingungen">
									<h4>7. Lieferbedingungen</h4>
								</Box>
								<p>
									7.1. Die Lieferbedingungen, der Liefertermin sowie
									gegebenenfalls bestehende Lieferbeschränkungen finden sich unter
									einer entsprechend bezeichneten Schaltfläche auf unserer
									Internetpräsenz oder im jeweiligen Angebot.
								</p>
								<p>
									7.2. Soweit Sie Verbraucher sind ist gesetzlich geregelt, dass
									die Gefahr des zufälligen Untergangs und der zufälligen
									Verschlechterung der verkauften Sache während der Versendung
									erst mit der Übergabe der Ware an Sie übergeht, unabhängig
									davon, ob die Versendung versichert oder unversichert erfolgt.
									Dies gilt nicht, wenn Sie eigenständig ein nicht vom Unternehmer
									benanntes Transportunternehmen oder eine sonst zur Ausführung
									der Versendung bestimmte Person beauftragt haben.
								</p>
								<p>
									7.3. Sind Sie Unternehmer, erfolgt die Lieferung und Versendung
									auf Ihre Gefahr.
								</p>
								<p>
									7.4. Scheitert die Zustellung der Ware aus Gründen, die der
									Kunde zu vertreten hat, trägt der Kunde die dem Verkäufer
									hierdurch entstehenden angemessenen Kosten. Dies gilt im
									Hinblick auf die Kosten für die Hinsendung nicht, wenn der Kunde
									sein Widerrufsrecht wirksam ausübt. Für die Rücksendekosten gilt
									bei wirksamer Ausübung des Widerrufsrechts durch den Kunden die
									in derWiderrufsbelehrung des Verkäufers hierzu getroffene
									Regelung.
								</p>
								<p>
									7.5. Der Verkäufer übernimmt kein Beschaffungsrisiko und ist
									berechtigt, von einem bereits geschlossenen Vertrag
									zurückzutreten, soweit eine rechtzeitige Beschaffung trotz
									vorherigen Abschlusses eines entsprechenden Einkaufsvertrages
									aus nicht von ihm zu vertretenden Gründen scheitert. Die
									Verantwortlichkeit des Verkäufers für Vorsatz oder
									Fahrlässigkeit bleibt unberührt.Der Verkäufer wird in diesem
									Fall den Kunden unverzüglich über die Nichtverfügbarkeit
									informieren und diesem eine bereits erbrachte Gegenleistung
									unverzüglich erstatten, oder behält sich für diesen Fall vor,
									eine preislich und qualitativ gleichwertige Ware anzubieten, mit
									dem Ziel, einen neuen Vertrag über den Kauf der preislich und
									qualitativ gleichartigen Ware abzuschließen.
								</p>
								<Box my={3} id="gesetzliche-maengelhaftungsrecht">
									<h4>8. Gesetzliches Mängelhaftungsrecht</h4>
								</Box>
								<p>
									Die Mängelhaftung richtet sich nach der Regelung
									&quot;Gewährleistung&quot; in unseren Allgemeinen
									Geschäftsbedingungen (Teil I).
								</p>
								<Box my={3}>
									<p>
										Diese AGB und Kundeninformationen wurden von den auf
										IT-Recht spezialisierten Juristen des Händlerbundes erstellt
										und werden permanent auf Rechtskonformität geprüft. Die
										Händlerbund Management AG garantiert für die
										Rechtssicherheit der Texte und haftet im Falle von
										Abmahnungen. Nähere Informationen dazu finden Sie unter:{" "}
										<a
											href="https://www.haendlerbund.de/agb-service"
											target="_blank"
											rel="noopener noreferrer"
										>
											Händlerbund.de
										</a>
										.
									</p>
								</Box>
								<Box mb={3} id="zahlung-per-rechnung-und-finanzierung">
									<h4>9. Zahlung per Rechnung und Finanzierung</h4>
								</Box>
								<p>
									In Zusammenarbeit mit Klarna Bank AB (publ){" "}
									<a
										href="https://www.klarna.com/de/"
										target="_blank"
										rel="noopener noreferrer"
									>
										https://www.klarna.com/de/
									</a>
									, Sveavägen 46, 111 34 Stockholm, Schweden, bieten wir Ihnen den
									Rechnungskauf und den Ratenkauf als Zahlungsoption an. Bitte
									beachten Sie, dass Kauf auf Rechnung und Ratenkauf nur für
									Verbraucher verfügbar sind und dass die Zahlung jeweils an
									Klarna zu erfolgen hat.
								</p>
								<Box my={3}>
									<p>Rechnung</p>
								</Box>
								<p>
									Beim Kauf auf Rechnung mit Klarna bekommen Sie immer zuerst die
									Ware und Sie haben immer eine Zahlungsfrist von 14 Tagen. Die
									vollständigen AGB zum Rechnungskauf für Lieferungen nach
									Deutschland finden Sie{" "}
									<a
										href="https://cdn.klarna.com/1.0/shared/content/legal/terms/EID/de_de/invoice?fee=[x]"
										target="_blank"
										rel="noopener noreferrer"
									>
										hier
									</a>{" "}
									und die vollständigen AGB zum Rechnungskauf für Lieferungen nach
									Österreich finden Sie{" "}
									<a
										href="https://cdn.klarna.com/1.0/shared/content/legal/terms/EID/de_at/invoice?fee=[x]"
										target="_blank"
										rel="noopener noreferrer"
									>
										hier
									</a>
									.
								</p>
								<Box my={3}>
									<p>Ratenkauf</p>
								</Box>
								<p>
									Mit dem Finanzierungsservice von Klarna können Sie Ihren Einkauf
									flexibel in monatlichen Raten von mindestens 1/24 des
									Gesamtbetrages (mindestens jedoch 6,95 €) oder unter den sonst
									in der Kasse angegebenen Bedingungen bezahlen. Weitere
									Informationen zum Ratenkauf einschließlich der Allgemeinen
									Geschäftsbedingungen und der europäischen Standardinformationen
									für Verbraucherkredite finden Sie{" "}
									<a
										href="https://cdn.klarna.com/1.0/shared/content/legal/terms/EID/de_de/account"
										target="_blank"
										rel="noopener noreferrer"
									>
										hier
									</a>
									. Ratenkauf wird nur für Deutschland angeboten.
								</p>
								<Box my={3}>
									<p>Datenschutzhinweis</p>
								</Box>
								<p>
									Klarna prüft und bewertet Ihre Datenangaben und pflegt bei
									berechtigtem Interesse und Anlass einen Datenaustausch mit
									anderen Unternehmen und Wirtschaftsauskunfteien. Ihre
									Personenangaben werden in übereinstimmung mit den geltenden
									Datenschutzbestimmungen und entsprechend den Angaben in Klarnas
									Datenschutzbestimmungen für: <br />
									<strong>Deutschland</strong>{" "}
									<a
										href="http://cdn.klarna.com/1.0/shared/content/legal/terms/0/de_de/privacy"
										target="_blank"
										rel="noopener noreferrer"
									>
										(Klarna/privacy.de)
									</a>
									<br />
									<strong>Österreich</strong>{" "}
									<a
										href="http://cdn.klarna.com/1.0/shared/content/legal/terms/0/de_at/privacy"
										target="_blank"
										rel="noopener noreferrer"
									>
										(Klarna/privacy.at)
									</a>{" "}
									behandelt.
								</p>
								<Box my={3}>
									<h4>III. Widerrufsbelehrung</h4>
								</Box>
								<p>
									Unsere Widerrufsbelehrung finden Sie{" "}
									<Link href="/help/cancellation-policy">hier</Link>
								</p>
								<p>Letzte Aktualisierung: 24.1.2023</p>
							</Grid>
						</Grid>
					</Container>
				</Box>
			</div>
		</>
	)
}

export default TermsAndConditionsPage
